/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type StoreApi, type UseBoundStore } from 'zustand';
import { createSelector } from 'reselect';
import { debounce, has, get, type DebouncedFunc } from 'lodash-es';
import diff, { type Diff } from 'deep-diff';

import { isFunction, isObject, getPayloadByFormatter } from '../utils';
import {
  DebounceTime,
  type AutosaveObserverConfig,
  type UseStoreType,
  type PathType,
  type AutosaveObserverProps,
  type AutosaveScopeState,
  type AutosaveStatus,
} from '../type/index';

export class AutosaveObserver<StoreType, ScopeKey, ScopeStateType> {
  private store: UseBoundStore<StoreApi<StoreType>>;

  public lock = false;
  public debouncedSaveFunc?: DebouncedFunc<() => Promise<void>>;
  public nextState!: ScopeStateType;
  public prevState!: ScopeStateType;
  public status: AutosaveStatus = 'clean';
  public revision = 0;
  public savedRevision = 0;
  public lastError?: Error;

  private diff: Diff<ScopeStateType, ScopeStateType>[] = [];
  private unsubscribe!: () => void;
  private saveQueue: Promise<void> = Promise.resolve();
  private lastRequestedRevision = -1;
  public config: AutosaveObserverConfig<StoreType, ScopeKey, ScopeStateType>;

  constructor(
    props: AutosaveObserverProps<StoreType, ScopeKey, ScopeStateType>,
  ) {
    const { store, ...config } = props;
    this.store = store;
    this.config = config;
    this.initSubscribe();
  }

  private initSubscribe = () => {
    const memoizeSelector = this.getMemoizeSelector();

    this.unsubscribe = (
      this.store as unknown as UseStoreType<StoreType, ScopeStateType>
    ).subscribe(memoizeSelector, this.subscribeCallback);
  };

  private getMemoizeSelector = () => {
    if (typeof this.config.selector === 'function') {
      return this.config.selector;
    }
    const { deps, transformer } = this.config.selector;
    return createSelector(deps, transformer);
  };

  private readCurrentState = () => {
    const selector = this.getMemoizeSelector() as (
      state: StoreType,
    ) => ScopeStateType;
    return selector(this.store.getState());
  };

  private getScope = () => this.config.scope ?? String(this.config.key);

  private subscribeCallback = async (
    nextState: ScopeStateType,
    prevState: ScopeStateType,
  ) => {
    this.nextState = nextState;
    this.prevState = prevState;

    if (this.lock) {
      return;
    }

    const diffChange = diff(prevState, nextState);
    if (!diffChange) {
      return;
    }

    this.revision += 1;
    this.diff = diffChange;
    this.status = 'pending';
    this.lastError = undefined;
    void this.config.eventCallBacks?.onDirty?.({
      key: this.config.key,
      scope: this.getScope(),
      data: nextState,
      revision: this.revision,
    });

    this.debouncedSaveFunc?.cancel?.();
    const delayTime = this.getTriggerDelayTime(prevState, diffChange);
    if (delayTime === 0 || this.config.immediate) {
      await this.parsedSaveFunc().catch(() => undefined);
      return;
    }

    this.debouncedSaveFunc = debounce(() => this.requestSave(), delayTime);
    void this.debouncedSaveFunc();
  };

  private parsedSaveFunc = () => this.requestSave();

  private requestSave = async () => {
    const targetRevision = this.revision;
    if (targetRevision <= this.lastRequestedRevision) {
      return this.saveQueue;
    }

    this.lastRequestedRevision = targetRevision;
    const params = this.nextState;
    const changes = this.diff;
    const operation = this.saveQueue
      .catch(() => undefined)
      .then(() => this.persistRevision(targetRevision, params, changes));
    this.saveQueue = operation;
    return operation;
  };

  private persistRevision = async (
    revision: number,
    params: ScopeStateType,
    changes: Diff<ScopeStateType, ScopeStateType>[],
  ) => {
    if (revision === this.revision) {
      this.status = 'saving';
    }
    try {
      await this.config.eventCallBacks?.onSaving?.({
        key: this.config.key,
        scope: this.getScope(),
        data: params,
        revision,
      });
      const beforeSavePayload = await getPayloadByFormatter<ScopeStateType>(
        params,
        this.config.middleware?.onBeforeSave,
      );
      const eventParams = {
        key: this.config.key,
        scope: this.getScope(),
        data: beforeSavePayload,
        revision,
      };
      await this.config.eventCallBacks?.onBeforeSave?.(eventParams);
      await this.config.saveRequest(
        beforeSavePayload,
        this.config.key,
        changes,
      );

      const afterSavePayload = await getPayloadByFormatter<ScopeStateType>(
        params,
        this.config.middleware?.onAfterSave,
      );
      this.savedRevision = Math.max(this.savedRevision, revision);
      if (revision === this.revision) {
        this.status = 'clean';
        this.lastError = undefined;
      }
      const successParams = {
        key: this.config.key,
        scope: this.getScope(),
        data: afterSavePayload,
        revision,
      };
      await this.config.eventCallBacks?.onSuccess?.(successParams);
      await this.config.eventCallBacks?.onAfterSave?.(successParams);
    } catch (caught) {
      const error =
        caught instanceof Error ? caught : new Error(String(caught));
      if (revision === this.revision) {
        this.status = 'failed';
        this.lastError = error;
      }
      this.config.eventCallBacks?.onError?.({
        key: this.config.key,
        scope: this.getScope(),
        error,
        revision,
      });
      throw error;
    }
  };

  public flush = async () => {
    const flushed = this.debouncedSaveFunc?.flush?.();
    if (flushed) {
      await flushed;
    }
    if (
      this.status === 'pending' &&
      this.revision > this.lastRequestedRevision
    ) {
      await this.requestSave();
    } else {
      await this.saveQueue;
    }
    if (this.status === 'failed') {
      throw this.lastError ?? new Error('Autosave failed');
    }
  };

  public retryFailed = async () => {
    if (this.status !== 'failed') {
      return;
    }
    this.nextState = this.readCurrentState();
    this.revision += 1;
    this.diff = [];
    this.status = 'pending';
    this.lastError = undefined;
    await this.config.eventCallBacks?.onDirty?.({
      key: this.config.key,
      scope: this.getScope(),
      data: this.nextState,
      revision: this.revision,
    });
    await this.requestSave();
  };

  public getScopeState = (): AutosaveScopeState<ScopeKey> => ({
    key: this.config.key,
    scope: this.getScope(),
    status: this.status,
    revision: this.revision,
    savedRevision: this.savedRevision,
    error: this.lastError,
  });

  public close = () => {
    this.debouncedSaveFunc?.cancel();
    this.unsubscribe();
    this.config.unobserver?.();
  };

  private getTriggerDelayTime = (
    prevState?: ScopeStateType,
    diffChange?: Diff<ScopeStateType, ScopeStateType>[],
  ) => {
    const configDebounce = this.config.debounce;

    if (!configDebounce) {
      return DebounceTime.Immediate;
    }
    if (isFunction(configDebounce)) {
      return configDebounce();
    }
    if (!isObject(configDebounce)) {
      return configDebounce;
    }
    if (!diffChange || diffChange.length === 0) {
      return configDebounce.default;
    }

    const targetDelayTimes: number[] = [];
    for (const change of diffChange) {
      const changePath = change.path;
      const debouncePath = this.getdebouncePath(changePath);
      if (
        !changePath ||
        !has(prevState, changePath) ||
        typeof debouncePath === 'number'
      ) {
        targetDelayTimes.push(configDebounce.default);
        continue;
      }
      const debounceType = get(
        configDebounce,
        debouncePath,
        configDebounce.default,
      );
      if (!isObject(debounceType)) {
        targetDelayTimes.push(debounceType);
        continue;
      }
      if (!debounceType.arrayType) {
        targetDelayTimes.push(configDebounce.default);
        continue;
      }
      if (!isObject(debounceType.action as DebounceTime)) {
        targetDelayTimes.push(debounceType.action as DebounceTime);
      } else {
        const kind =
          change.kind === 'A' && change.item?.kind
            ? change.item.kind
            : change.kind;
        targetDelayTimes.push(debounceType.action[kind]);
      }
    }
    return Math.min(...targetDelayTimes);
  };

  private getdebouncePath = (changePath?: PathType[]) => {
    if (!changePath) {
      return '';
    }
    if (changePath.some(path => typeof path === 'number')) {
      return changePath[0];
    }
    return changePath.join('.');
  };
}
